/**
 * The product portal's three-zone shell: a persistent left rail, the main column, and a right
 * rail neither surface uses. No hamburger, no drawer — the rail is always visible.
 *
 * **The scope root lives here, and it is load-bearing.** `root` carries `tunet-surface`, which
 * is the only selector `src/shared-web/styles.css` matches under. This bundle's `index.html`
 * holds the VR client's own `<style>` block, its own `:root` tokens and `color-scheme: dark` —
 * the portal and the room are one document. Putting the shared sheet's tokens on this element
 * rather than at `:root` is what keeps a portal restyle from repainting the splash screen, the
 * in-session screen and the status overlays. `scoping.test.ts` is the check.
 *
 * `portal-layer` is the second class and belongs to this bundle alone: `body` is
 * `height: 100%; overflow: hidden` once the canvas is live, so the portal has to be a fixed
 * full-viewport layer that scrolls internally, exactly as `.splash` and `.auth-layer` already do.
 * The admin bundle needs none of that and does not have the class.
 *
 * **The rail is sparse and stays sparse.** The reference dashboards carry seven to twelve
 * destinations; this portal has three — four for an org owner, who also gets People — plus the
 * room itself. plan/portal-design.md adopts the
 * rail anyway with the argument stated: a rail that grows is cheaper than a top bar that has to
 * be replaced by one, and the top bar is exactly what is being replaced here.
 *
 * **The bottom slot holds who is signed in and the way out — no upgrade CTA.** `Plan` is schema
 * and seed data; nothing enforces `RoomQuota` or `AiTurnQuotaPerWeek` and there is no billing.
 * A fake upgrade path is worse than an empty slot.
 *
 * **The rail-foot also carries the one self-service control every account gets: renaming
 * itself** (`plan/open-items.md`, "Nobody can edit a display name"). It lives here rather than
 * on a page of its own because every portal page already wears this shell, so a control that
 * belongs to "the account, wherever you are" only has to be built once. Renaming does not touch
 * `account.email` — the address is the identity now (`AppUser.Email`), and this route only ever
 * changes the label shown beside it. On success `account.displayName` is mutated on the same
 * object every page holds a reference to (`main.ts`'s module-level `account`), so navigating to
 * another portal page shows the new name without a second round trip — see this file's own
 * `PortalShellDeps.updateOwnDisplayName` doc for why that mutation is safe here specifically.
 */

import type { AuthUserDto } from '../../generated/contracts';
import { signOut } from '../../core/AccountGate';
import { NotSignedInError, updateOwnDisplayName } from '../../net/api';
import { el, replace } from './dom';

// The SHARED stylesheet — one file, consumed verbatim by this bundle and by src/client-admin
// (plan/portal-design.md: share a stylesheet, not components). Imported here rather than from
// main.ts so it rides with the portal's own module: nothing on the `/room` route imports this
// file, and a stylesheet whose every selector needs `.tunet-surface` costs the room nothing
// either way.
import '../../../../shared-web/styles.css';

// This bundle's own portal rules — the exports viewer, the controls list, the room table's
// layout. Not shared, because the admin surface has no equivalent of any of it. Extracted out
// of index.html's <style> block, which plan/portal-design.md names as a prerequisite that
// commits to nothing.
import './portal.css';

export type PortalRoute = '/portal' | '/portal/people' | '/portal/exports' | '/portal/controls';

export interface PortalShellNav {
  /** Navigate to one of the portal's own routes. */
  go(route: PortalRoute): void;
  /** Leave the portal for the room itself. Optional — the room may be unreachable. */
  enterRoom?: (() => void) | null;
}

export interface PortalShellDeps {
  /**
   * `net/api.ts`'s `updateOwnDisplayName` by default. Injected so a test can drive both the
   * success and the failure path without a real network call — the same shape every other
   * portal page's own `deps` parameter already uses (`PortalPeopleDeps`, `PortalHomeDeps`).
   */
  updateOwnDisplayName: typeof updateOwnDisplayName;
}

const defaultShellDeps: PortalShellDeps = { updateOwnDisplayName };

export class PortalShell {
  readonly root: HTMLElement;
  readonly main: HTMLElement;

  private readonly nav: HTMLElement;
  private readonly deps: PortalShellDeps;
  private readonly foot: HTMLElement;

  /** Whether the rail-foot is currently showing the rename form rather than the plain name. */
  private renaming = false;

  constructor(
    host: HTMLElement,
    private readonly account: AuthUserDto,
    /**
     * Which rail item is the current page, or `null` for a page that wears this shell without
     * being a rail destination — the diagram viewer (plan/diagram-extraction.md, A5) is the
     * first: it is reached from a room row rather than from the rail, and highlighting
     * "Overview" while standing somewhere else would be a lie about where you are.
     */
    private readonly active: PortalRoute | null,
    private readonly navigate: PortalShellNav,
    /** The org's name, once `GET /api/portal/overview` has answered. Absent until then. */
    orgName: string | null = null,
    /**
     * Whether to show the People destination, which is an org owner's.
     *
     * **Presentation, not protection.** Every route that page calls answers a 403 to a plain
     * member and the server is what enforces that. This flag exists so a member is not shown a
     * destination that would only refuse them — and so the page does not fire a request it knows
     * will be refused, which teaches the next reader that the refusal is routine. It defaults to
     * hidden and is turned on once `GET /api/portal/overview` has said `isOwner`, because a rail
     * item that appears and then vanishes is worse than one that appears late.
     */
    private showPeople = false,
    deps: Partial<PortalShellDeps> = {},
  ) {
    this.deps = { ...defaultShellDeps, ...deps };
    this.nav = el('nav', { class: 'rail-nav', 'aria-label': 'Sections' });
    this.main = el('main', { class: 'main' });
    this.foot = el('div', { class: 'rail-foot' });

    this.root = el('div', { class: 'tunet-surface portal-layer' }, [
      el('div', { class: 'shell' }, [
        el('aside', { class: 'rail' }, [
          el('div', { class: 'rail-wordmark' }, [
            el('strong', { text: 'Tunet' }),
            el('span', { class: 'rail-org', text: orgName ?? '' }),
          ]),
          this.nav,
          this.foot,
        ]),
        this.main,
      ]),
    ]);

    this.renderNav();
    this.renderFoot();
    host.appendChild(this.root);
  }

  /** Whether the caller is their org's owner arrives with the overview, like the org name —
   *  after the shell is already on screen. Re-renders the nav in one pass rather than splicing
   *  a link in, so there is one place that decides what the rail contains. */
  setIsOwner(isOwner: boolean): void {
    if (this.showPeople === isOwner) {
      return;
    }

    this.showPeople = isOwner;
    this.renderNav();
  }

  /** The org name arrives with the overview, after the shell is already on screen. */
  setOrgName(orgName: string): void {
    const slot = this.root.querySelector('.rail-org');

    if (slot !== null) {
      slot.textContent = orgName;
    }
  }

  dispose(): void {
    this.root.remove();
  }

  /**
   * The rail-foot: the plain name/address, an "Edit" control beside it, and the sign-out button
   * underneath — or, mid-rename, an inline form in the name's place. Rebuilt from scratch rather
   * than patched in place, the same "one place decides what the rail contains" reasoning
   * `renderNav` already gives for the nav above it.
   */
  private renderFoot(): void {
    replace(
      this.foot,
      this.renaming
        ? this.renameForm()
        : el('div', { class: 'row', style: 'align-items: baseline; gap: 0.5rem' }, [
            el('span', {}, [
              el('strong', { text: this.account.displayName || this.account.email }),
              el('span', { text: this.account.email }),
            ]),
            el('button', {
              type: 'button',
              class: 'btn',
              'data-edit-display-name': 'true',
              'aria-label': 'Edit your display name',
              text: 'Edit',
            }, []),
          ]),
      el('div', { style: 'margin-top: 0.625rem' }, [this.signOutButton()]),
    );

    if (!this.renaming) {
      this.foot.querySelector('[data-edit-display-name]')?.addEventListener('click', () => {
        this.renaming = true;
        this.renderFoot();
      });
    }
  }

  private renameForm(): HTMLElement {
    const input = el('input', {
      type: 'text',
      class: 'field',
      'data-display-name-input': 'true',
      value: this.account.displayName,
      maxlength: '100',
      'aria-label': 'Your display name',
    });

    const save = el('button', { type: 'submit', class: 'btn btn-primary', 'data-display-name-save': 'true', text: 'Save' });
    const cancel = el('button', { type: 'button', class: 'btn', 'data-display-name-cancel': 'true', text: 'Cancel' });

    cancel.addEventListener('click', () => {
      this.renaming = false;
      this.renderFoot();
    });

    const form = el('form', { class: 'table-tools', 'data-display-name-form': 'true' }, [
      el('div', { class: 'row' }, [input, save, cancel]),
    ]);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitRename(input, save);
    });

    return form;
  }

  private async submitRename(input: HTMLInputElement, submit: HTMLButtonElement): Promise<void> {
    const name = input.value.trim();

    if (name.length === 0) {
      return;
    }

    submit.disabled = true;

    try {
      const updated = await this.deps.updateOwnDisplayName(name);

      // Mutated on the same object every portal page holds — see this file's own module
      // comment for why that is what makes the new name show up on the next page navigation
      // with no second read.
      this.account.displayName = updated.displayName;
      this.renaming = false;
      this.renderFoot();
    } catch (error) {
      if (error instanceof NotSignedInError) {
        void signOut();
        return;
      }

      submit.disabled = false;
      submit.after(el('span', { class: 'muted', text: describeShellError(error, "Couldn't save that name.") }));
    }
  }

  private signOutButton(): HTMLElement {
    const button = el('button', { type: 'button', class: 'btn', 'data-logout': 'true', text: 'Sign out' });

    // `signOut()` (core/AccountGate) posts to /api/auth/logout and then does a full navigation
    // back to /portal, rather than resetting state in place: this route can have a live RoomHub
    // connection, a running Babylon engine or an open WebXR session underneath it, and tearing
    // all of that down safely is a bigger job than a sign-out button justifies.
    button.addEventListener('click', () => void signOut());

    return button;
  }

  private renderNav(): void {
    const items: { route: PortalRoute; label: string }[] = [
      { route: '/portal', label: 'Overview' },
      ...(this.showPeople ? [{ route: '/portal/people' as const, label: 'People' }] : []),
      { route: '/portal/exports', label: 'Exports' },
      { route: '/portal/controls', label: 'Controls' },
    ];

    const links = items.map((item) => {
      const link = el(
        'a',
        {
          class: 'rail-link',
          href: item.route,
          // `aria-current="page"` is both the accessible state and the CSS hook — the accent's
          // active-nav job hangs off exactly this attribute, so there is no second class to
          // keep in step with it.
          'aria-current': item.route === this.active ? 'page' : null,
        },
        [el('span', { text: item.label })],
      );

      link.addEventListener('click', (event) => {
        // Left-click only, and never with a modifier held: a ctrl-click on a rail item should
        // open a real tab, which it can, because these are real hrefs and the server serves the
        // SPA for any of them.
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
          return;
        }

        event.preventDefault();
        this.navigate.go(item.route);
      });

      return link;
    });

    // Entering the room is not a rail destination and is not styled as one: it leaves the
    // portal entirely, tears this shell down and starts a Babylon scene. It is the one filled
    // button on the page — the accent's second job — and it is absent rather than disabled when
    // there is no room to enter, because a disabled primary action is a question nobody can
    // answer from looking at it.
    const enter = this.navigate.enterRoom;

    replace(
      this.nav,
      ...links,
      enter ? el('div', { class: 'rail-enter' }, [this.enterButton(enter)]) : null,
    );
  }

  private enterButton(enterRoom: () => void): HTMLElement {
    const button = el('button', {
      type: 'button',
      class: 'btn btn-primary',
      'data-enter-room': 'true',
      text: 'Enter VR',
    });

    button.addEventListener('click', () => enterRoom());

    return button;
  }
}

/**
 * A page heading, in the fixed shape every portal page uses.
 *
 * **`blurb` is optional, and that is plan/portal-design.md's second rule expressed where it can
 * be enforced.** A page gets at most one line saying what it is for, and only when that line
 * says something the title does not. "Overview" needs no sentence explaining that it is an
 * overview. Passing nothing is the ordinary case rather than the degenerate one.
 */
export function pageHead(title: string, blurb?: string | null): HTMLElement {
  return el('header', { class: 'page-head' }, [
    el('h1', { text: title }),
    blurb ? el('p', { text: blurb }) : null,
  ]);
}

export function card(children: (Node | string | false | null)[]): HTMLElement {
  return el('section', { class: 'card section' }, children);
}

export function cardHead(title: string, note?: string | null): HTMLElement {
  return el('header', { class: 'card-head' }, [
    el('h2', { text: title }),
    note ? el('p', { text: note }) : null,
  ]);
}

/**
 * One KPI tile.
 *
 * **`context` is not optional, and that is the whole design.** plan/portal-design.md restates
 * the reference set's "no number appears without a comparison" as "no number without CONTEXT" —
 * a delta, a denominator, a recency, or a breakdown — precisely because a literal delta is
 * unavailable for almost everything this product stores. A tile with none of the four does not
 * get an empty slot and does not get an invented comparison; it stops being a tile and becomes a
 * row in a table. Making the parameter required is that rule expressed where it can be enforced.
 */
export function kpi(options: {
  label: string;
  value: string;
  context: string;
  direction?: import('./metrics').Direction;
  note?: string;
}): HTMLElement {
  return el('section', { class: 'card kpi' }, [
    el('div', { class: 'kpi-label', text: options.label }),
    el('div', { class: 'kpi-value', text: options.value }),
    el('div', {
      class: 'kpi-context',
      text: options.context,
      // Absent rather than "flat" when there is no direction to state: red and green are
      // reserved for deltas, and a denominator or a recency is not one.
      'data-direction': options.direction ?? null,
    }),
    options.note ? el('div', { class: 'kpi-note', text: options.note }) : null,
  ]);
}

function describeShellError(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.length > 0 ? error.message : fallback;
}
